import { isNull, isUndefined } from 'lodash-es';
import {
  type MouseEvent,
  type MutableRefObject,
  type TouchEvent,
  type TouchList as ReactTouchList,
  useRef,
} from 'react';

import {
  PINCH_RELATIVE_THRESHOLD,
  RIGHT_BUTTON_ID,
  SWIPE_RELATIVE_THRESHOLD,
} from '../constants.js';
import {
  type Click,
  type DblClick,
  type DblTap,
  type DragEnd,
  type DragEndProps,
  type DragMove,
  type DragMoveProps,
  type DragStart,
  type DragStartProps,
  type Idle,
  isDragMove,
  isMiddleButtonDragMove,
  isPinchGesture,
  isPinchStartGesture,
  isSwipeMove,
  isSwipeStart,
  type MiddleButtonDragEnd,
  type MiddleButtonDragMove,
  type MiddleButtonDragStart,
  type PinchEnd,
  type PinchEndProps,
  type PinchMove,
  type PinchMoveProps,
  type PinchStart,
  type PinchStartProps,
  PointerEventActions,
  type PointerEventModifiers,
  type PointerEventPosition,
  type PointerEvents,
  type PointerEventsHandler,
  type RelativeAbsolutePosition,
  type SwipeDirections,
  type SwipeEnd,
  type SwipeEndProps,
  type SwipeMove,
  type SwipeMoveProps,
  type SwipeStart,
  type SwipeStartProps,
  type Tap,
} from '../types/pointer-events.js';
import { calculateMousePositions } from '../utils/calculate-mouse-positions.js';
import {
  buildDragEndEvent,
  buildDragMoveEvent,
  buildDragStartEvent,
} from '../utils/drag-event-helpers.js';
import { getTouchesDistance } from '../utils/get-touches-distance.js';

const isIntentionalMovement = (
  startedHoldingAt: Required<RelativeAbsolutePosition>,
  position: Required<RelativeAbsolutePosition>,
) =>
  position.relative.x > startedHoldingAt.relative.x + 5 ||
  position.relative.x < startedHoldingAt.relative.x - 5 ||
  position.relative.y > startedHoldingAt.relative.y + 5 ||
  position.relative.y < startedHoldingAt.relative.y - 5;

const extractEventModifiers = ({
  altKey,
  ctrlKey,
  metaKey,
  shiftKey,
}: MouseEvent | TouchEvent) => ({
  altKey,
  ctrlKey,
  metaKey,
  shiftKey,
});

type UseGesturesProps = {
  onDragStart: PointerEventsHandler<DragStartProps>;
  onDragMove: PointerEventsHandler<DragMoveProps>;
  onDragEnd: PointerEventsHandler<DragEndProps>;

  onDragStartWithMiddleButton: PointerEventsHandler<DragStartProps>;
  onDragMoveWithMiddleButton: PointerEventsHandler<DragMoveProps>;
  onDragEndWithMiddleButton: PointerEventsHandler<DragEndProps>;

  onTouchStart: PointerEventsHandler<PointerEventPosition>;

  onTap: PointerEventsHandler<PointerEventPosition>;
  onDoubleTap: PointerEventsHandler<PointerEventModifiers>;

  onPinchStart: PointerEventsHandler<PinchStartProps>;
  onPinchMove: PointerEventsHandler<PinchMoveProps>;
  onPinchEnd: PointerEventsHandler<PinchEndProps>;

  onSwipeStart: PointerEventsHandler<SwipeStartProps>;
  onSwipeMove: PointerEventsHandler<SwipeMoveProps>;
  onSwipeEnd: PointerEventsHandler<SwipeEndProps>;

  onMouseClick: PointerEventsHandler<PointerEventPosition>;
  onMouseDoubleClick: PointerEventsHandler<PointerEventPosition>;

  onMouseHover: PointerEventsHandler<PointerEventPosition>;

  eventCancelled: MutableRefObject<boolean>;
};

export const usePointerEvents = ({
  onDoubleTap,
  onDragEnd,
  onDragMove,
  onDragStart,
  onMouseClick,
  onMouseDoubleClick,
  onPinchEnd,
  onPinchMove,
  onPinchStart,
  onTap,
  onMouseHover,
  onTouchStart,
  onDragStartWithMiddleButton,
  onDragMoveWithMiddleButton,
  onDragEndWithMiddleButton,
  onSwipeStart,
  onSwipeMove,
  onSwipeEnd,
  eventCancelled,
}: Partial<UseGesturesProps>) => {
  const currentEventRef = useRef<PointerEvents>({
    action: PointerEventActions.Idle,
  });

  const startedHoldingAtRef = useRef<RelativeAbsolutePosition | null>(null);
  const startedTouchesAtRef = useRef<ReactTouchList | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerDblClickTimeout = () => {
    timeoutRef.current = setTimeout(() => {
      clearDblClickTimeout();
    }, 200);
  };
  const clearDblClickTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const dispatchHoldAndDragStart = (
    { action, ...payload }: MiddleButtonDragStart | DragStart,
    eventModifiers: PointerEventModifiers,
  ) => {
    if (action === PointerEventActions.MiddleButtonDragStart) {
      onDragStartWithMiddleButton?.({ ...payload, ...eventModifiers });
    } else {
      onDragStart?.({ ...payload, ...eventModifiers });
    }
  };

  const dispatchHoldAndDragMove = (
    { action, ...payload }: MiddleButtonDragMove | DragMove,
    eventModifiers: PointerEventModifiers,
  ) => {
    if (action === PointerEventActions.MiddleButtonDragMove) {
      onDragMoveWithMiddleButton?.({ ...payload, ...eventModifiers });
    } else {
      onDragMove?.({ ...payload, ...eventModifiers });
    }
  };

  const dispatchHoldAndDragEnd = (
    { action, ...payload }: MiddleButtonDragEnd | DragEnd,
    eventModifiers: PointerEventModifiers,
  ) => {
    if (action === PointerEventActions.MiddleButtonDragEnd) {
      onDragEndWithMiddleButton?.({ ...payload, ...eventModifiers });
    } else {
      onDragEnd?.({ ...payload, ...eventModifiers });
    }
  };

  const getGestureAndCallback = (createTouchEvents: boolean) => {
    const callbackOnce = createTouchEvents ? onTap : onMouseClick;
    const callbackTwice = createTouchEvents ? onDoubleTap : onMouseDoubleClick;
    const clickedOnceAction:
      | PointerEventActions.Tap
      | PointerEventActions.Click = createTouchEvents
      ? PointerEventActions.Tap
      : PointerEventActions.Click;
    const clickedTwiceAction:
      | PointerEventActions.DblTap
      | PointerEventActions.DblClick = createTouchEvents
      ? PointerEventActions.DblTap
      : PointerEventActions.DblClick;
    return {
      callbackOnce,
      callbackTwice,
      clickedOnceAction,
      clickedTwiceAction,
    };
  };

  function handleAmountOfClicks(
    mousePosition: RelativeAbsolutePosition,
    createTouchEvents: false,
    eventModifiers: PointerEventModifiers,
  ): DblClick | Click;

  function handleAmountOfClicks(
    mousePosition: RelativeAbsolutePosition,
    createTouchEvents: true,
    eventModifiers: PointerEventModifiers,
  ): Tap | DblTap;

  function handleAmountOfClicks(
    mousePosition: RelativeAbsolutePosition,
    createTouchEvents: boolean = false,
    eventModifiers: PointerEventModifiers,
  ): Tap | DblTap | DblClick | Click {
    let gesture: Tap | DblTap | DblClick | Click;
    const {
      callbackOnce,
      callbackTwice,
      clickedOnceAction,
      clickedTwiceAction,
    } = getGestureAndCallback(createTouchEvents);

    if (isNull(timeoutRef.current)) {
      callbackOnce?.({ position: mousePosition, ...eventModifiers });
      triggerDblClickTimeout();
      gesture = {
        action: clickedOnceAction,
        position: mousePosition,
      };
    } else {
      clearDblClickTimeout();
      callbackTwice?.({ position: mousePosition, ...eventModifiers });
      gesture = {
        action: clickedTwiceAction,
        position: mousePosition,
      };
    }

    return gesture;
  }

  const handleMouseDown = (mouseEvent: MouseEvent<HTMLDivElement>) => {
    if (mouseEvent.buttons === RIGHT_BUTTON_ID) {
      return;
    }

    const mousePosition = calculateMousePositions(
      mouseEvent,
      mouseEvent.currentTarget,
    );

    if (isUndefined(mousePosition)) {
      return;
    }

    startedHoldingAtRef.current = mousePosition;
  };

  const handleMouseMove = (mouseEvent: MouseEvent<HTMLDivElement>) => {
    if (mouseEvent.buttons === RIGHT_BUTTON_ID) {
      return;
    }

    const position = calculateMousePositions(
      mouseEvent,
      mouseEvent.currentTarget,
    );

    if (isUndefined(position)) {
      return;
    }

    let gesture: PointerEvents;

    if (
      isDragMove(currentEventRef.current) ||
      isMiddleButtonDragMove(currentEventRef.current)
    ) {
      gesture = buildDragMoveEvent(
        mouseEvent.buttons,
        currentEventRef.current.startHoldingAt,
        currentEventRef.current.currentPosition,
        position,
      );

      dispatchHoldAndDragMove(gesture, extractEventModifiers(mouseEvent));
    } else if (
      startedHoldingAtRef.current &&
      isIntentionalMovement(startedHoldingAtRef.current, position)
    ) {
      const eventToDispatch = buildDragStartEvent(
        mouseEvent.buttons,
        startedHoldingAtRef.current,
      );

      dispatchHoldAndDragStart(
        eventToDispatch,
        extractEventModifiers(mouseEvent),
      );

      gesture = buildDragMoveEvent(
        mouseEvent.buttons,
        startedHoldingAtRef.current,
        startedHoldingAtRef.current,
        position,
      );
    } else {
      gesture = {
        action: PointerEventActions.Hover,
        position,
      };

      onMouseHover?.({ position, ...extractEventModifiers(mouseEvent) });
    }

    currentEventRef.current = gesture;
  };

  const handleMouseUp = (mouseEvent: MouseEvent<HTMLDivElement>) => {
    if (mouseEvent.buttons === RIGHT_BUTTON_ID) {
      return;
    }
    if (eventCancelled?.current) {
      eventCancelled.current = false;
      startedHoldingAtRef.current = null;
      currentEventRef.current = {
        action: PointerEventActions.Idle,
      };
      return;
    }

    const mousePosition = calculateMousePositions(
      mouseEvent,
      mouseEvent.currentTarget,
    );

    if (isUndefined(mousePosition)) {
      return;
    }

    if (
      isDragMove(currentEventRef.current) ||
      isMiddleButtonDragMove(currentEventRef.current)
    ) {
      const gesture = buildDragEndEvent(
        currentEventRef.current,
        currentEventRef.current.startHoldingAt,
        mousePosition,
      );

      dispatchHoldAndDragEnd(gesture, extractEventModifiers(mouseEvent));

      currentEventRef.current = { ...gesture };
    } else {
      currentEventRef.current = handleAmountOfClicks(
        mousePosition,
        false,
        extractEventModifiers(mouseEvent),
      );
    }

    startedHoldingAtRef.current = null;
  };

  const handleTouchStart = (touchEvent: TouchEvent) => {
    const { touches } = touchEvent;
    const mousePosition = calculateMousePositions(
      touches[0],
      touchEvent.currentTarget ?? touchEvent.nativeEvent.currentTarget,
    );

    if (isUndefined(mousePosition)) {
      return;
    }

    onTouchStart?.({
      position: mousePosition,
      ...extractEventModifiers(touchEvent),
    });

    startedHoldingAtRef.current = mousePosition;
    startedTouchesAtRef.current = touches;
  };

  const handleTouchMove = (touchEvent: TouchEvent) => {
    const { touches } = touchEvent;
    let gesture:
      | PinchStart
      | PinchMove
      | DragMove
      | DragStart
      | SwipeStart
      | SwipeMove
      | Idle = {
      action: PointerEventActions.Idle,
    };

    const amountOfTouches = touches.length;
    switch (amountOfTouches) {
      case 1: {
        const mousePosition = calculateMousePositions(
          touches[0],
          touchEvent.currentTarget ?? touchEvent.nativeEvent.currentTarget,
        );

        if (isUndefined(mousePosition)) {
          return;
        }

        if (isDragMove(currentEventRef.current)) {
          const { startHoldingAt, currentPosition: prevPosition } =
            currentEventRef.current;
          const delta = mousePosition.relative.x - prevPosition.relative.x;
          gesture = {
            action: PointerEventActions.DragMove,
            startHoldingAt,
            currentPosition: mousePosition,
            delta,
          };

          onDragMove?.({
            startHoldingAt,
            currentPosition: mousePosition,
            delta,
            ...extractEventModifiers(touchEvent),
          });
        } else if (
          startedHoldingAtRef.current &&
          isIntentionalMovement(startedHoldingAtRef.current, mousePosition)
        ) {
          const delta =
            mousePosition.relative.x - startedHoldingAtRef.current.relative.x;
          gesture = {
            action: PointerEventActions.DragMove,
            startHoldingAt: startedHoldingAtRef.current,
            currentPosition: mousePosition,
            delta,
          };

          onDragStart?.({
            startHoldingAt: startedHoldingAtRef.current,
            ...extractEventModifiers(touchEvent),
          });
        }
        break;
      }
      case 2: {
        const distance = getTouchesDistance(touches);
        if (
          isPinchGesture(currentEventRef.current) ||
          isPinchStartGesture(currentEventRef.current)
        ) {
          let delta, initialDistance;
          if (isPinchGesture(currentEventRef.current)) {
            const prevDistance = currentEventRef.current.distance;
            initialDistance = currentEventRef.current.initialDistance;
            delta = distance - prevDistance;
          } else {
            initialDistance = currentEventRef.current.distance;
            delta = distance - initialDistance;
          }
          const changeRatio = distance / initialDistance;

          const payload = {
            initialDistance,
            changeRatio,
            delta,
            distance,
          };
          gesture = {
            action: PointerEventActions.Pinch,
            ...payload,
          };

          onPinchMove?.({ ...payload, ...extractEventModifiers(touchEvent) });
        } else if (
          isSwipeStart(currentEventRef.current) ||
          isSwipeMove(currentEventRef.current)
        ) {
          const { position } = currentEventRef.current;
          const deltaXFingerZero = {
            deltaX: touches[0].clientX - position[0].clientX,
            deltaY: touches[0].clientY - position[0].clientY,
            id: touches[0].identifier,
          };
          const deltaXFingerOne = {
            deltaX: touches[1].clientX - position[1].clientX,
            deltaY: touches[1].clientY - position[1].clientY,
            id: touches[1].identifier,
          };

          const fingerZeroMovedMoreX =
            Math.abs(deltaXFingerZero.deltaX) >
            Math.abs(deltaXFingerOne.deltaX);
          const { deltaX } = fingerZeroMovedMoreX
            ? deltaXFingerZero
            : deltaXFingerOne;
          const fingerZeroMovedMoreY =
            Math.abs(deltaXFingerZero.deltaY) >
            Math.abs(deltaXFingerOne.deltaY);
          const { deltaY } = fingerZeroMovedMoreY
            ? deltaXFingerZero
            : deltaXFingerOne;

          let delta;
          let direction: SwipeDirections;
          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            delta = deltaY;
            direction = deltaY >= 0 ? 'down' : 'up';
          } else {
            delta = deltaX;
            direction = deltaX >= 0 ? 'right' : 'left';
          }
          const payload = {
            position: touchEvent.touches,
            delta,
            direction,
            deltaY,
            deltaX,
          };
          gesture = {
            action: PointerEventActions.SwipeMove,
            ...payload,
          };

          onSwipeMove?.({ ...payload, ...extractEventModifiers(touchEvent) });
        } else {
          if (startedTouchesAtRef.current) {
            const initialDistanceBetweenFingers = getTouchesDistance(
              startedTouchesAtRef.current,
            );
            const currentDistanceBetweenFingers = getTouchesDistance(touches);

            const delta = Math.abs(
              initialDistanceBetweenFingers - currentDistanceBetweenFingers,
            );

            const pinchThreshold =
              initialDistanceBetweenFingers * PINCH_RELATIVE_THRESHOLD;

            if (delta > pinchThreshold) {
              gesture = {
                action: PointerEventActions.PinchStart,
                distance,
              };
              onPinchStart?.({
                distance,
                ...extractEventModifiers(touchEvent),
              });
            } else {
              const deltaXFingerZero =
                startedTouchesAtRef.current[0].clientX - touches[0].clientX;
              const deltaYFingerZero =
                startedTouchesAtRef.current[0].clientY - touches[0].clientY;

              const delta = Math.max(
                Math.abs(deltaXFingerZero),
                Math.abs(deltaYFingerZero),
              );

              const swipeThreshold =
                initialDistanceBetweenFingers * SWIPE_RELATIVE_THRESHOLD;

              if (delta > swipeThreshold) {
                gesture = {
                  action: PointerEventActions.SwipeStart,
                  position: touches,
                };

                onSwipeStart?.({
                  position: touches,
                  ...extractEventModifiers(touchEvent),
                });
              }
            }
          }
        }
        break;
      }
    }

    currentEventRef.current = { ...gesture };
  };

  const handleTouchEnd = (touchEvent: TouchEvent) => {
    if (touchEvent.cancelable) {
      touchEvent.preventDefault();
    }

    let gesture: DragEnd | PinchEnd | SwipeEnd | Tap | DblTap | Idle = {
      action: PointerEventActions.Idle,
    };

    const { changedTouches } = touchEvent;

    const amountOfTouches = changedTouches.length;
    switch (amountOfTouches) {
      case 1: {
        const mousePosition = calculateMousePositions(
          changedTouches[0],
          touchEvent.currentTarget ?? touchEvent.nativeEvent.currentTarget,
        );

        if (isUndefined(mousePosition)) {
          return;
        }

        if (isDragMove(currentEventRef.current)) {
          const { startHoldingAt } = currentEventRef.current;
          gesture = {
            action: PointerEventActions.DragEnd,
            startHoldingAt,
            releasedAt: mousePosition,
          };

          onDragEnd?.({
            startHoldingAt,
            releasedAt: mousePosition,
            ...extractEventModifiers(touchEvent),
          });
        } else if (
          startedTouchesAtRef.current?.length === amountOfTouches &&
          startedTouchesAtRef.current[0].identifier ===
            changedTouches[0].identifier
        ) {
          gesture = handleAmountOfClicks(
            mousePosition,
            true,
            extractEventModifiers(touchEvent),
          );
        }
        startedHoldingAtRef.current = null;
        break;
      }
      case 2: {
        if (isPinchGesture(currentEventRef.current)) {
          const { action, ...rest } = currentEventRef.current;
          gesture = {
            ...rest,
            action: PointerEventActions.PinchEnd,
          };

          onPinchEnd?.({ ...rest, ...extractEventModifiers(touchEvent) });
        } else if (isSwipeMove(currentEventRef.current)) {
          const { action, ...rest } = currentEventRef.current;
          gesture = {
            ...rest,
            action: PointerEventActions.SwipeEnd,
          };

          onSwipeEnd?.({ ...rest, ...extractEventModifiers(touchEvent) });
        } else {
          gesture = {
            action: PointerEventActions.Idle,
          };
        }
        startedHoldingAtRef.current = null;
        break;
      }
    }

    currentEventRef.current = { ...gesture };
  };

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
